/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Link, breakpoints } from '@coursera/cds-core';
import { GlobeIcon } from '@coursera/cds-icons';

import { FacebookIcon } from 'bundles/partners-graphql/components/cds/icons/FacebookIcon';
import { XIcon } from 'bundles/partners-graphql/components/cds/icons/XIcon';
import { YouTubeIcon } from 'bundles/partners-graphql/components/cds/icons/YouTubeIcon';

const styles = {
  linksGrid: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-600);

    ${breakpoints.down('sm')} {
      flex-direction: column;
      gap: var(--cds-spacing-200);
    }
  `,
  linkItem: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);

    svg {
      flex-shrink: 0;
    }
  `,
};

type PartnerLinks = {
  website?: string | null;
  twitter?: string | null;
  facebook?: string | null;
  youtube?: string | null;
};

type Props = {
  links: PartnerLinks | null | undefined;
};

const formatWebsiteUrl = (url: string): string => {
  // Remove protocol, hash fragment, and trailing slash for display
  return url
    .replace(/^https?:\/\//, '')
    .replace(/#.*$/, '')
    .replace(/\/$/, '');
};

/**
 * Returns href as-is if it already starts with http(s), otherwise prepends baseUrl.
 */
const getLinkHref = (value: string, baseUrl: string): string =>
  /^https?:\/\//i.test(value) ? value : `${baseUrl}${value.replace(/^\/+/, '')}`;

const PartnerLinksSection = ({ links }: Props) => {
  if (!links) {
    return null;
  }

  const { website, twitter, facebook, youtube } = links;
  const hasAnyLink = website || twitter || facebook || youtube;

  if (!hasAnyLink) {
    return null;
  }

  return (
    <div css={styles.linksGrid}>
      {website && (
        <Link
          href={website}
          variant="standardSecondary"
          target="_blank"
          rel="noopener noreferrer"
          css={styles.linkItem}
        >
          <GlobeIcon size="medium" />
          {formatWebsiteUrl(website)}
        </Link>
      )}

      {facebook && (
        <Link
          href={getLinkHref(facebook, 'https://www.facebook.com/')}
          variant="standardSecondary"
          target="_blank"
          rel="noopener noreferrer"
          css={styles.linkItem}
        >
          <FacebookIcon />
          {facebook}
        </Link>
      )}

      {twitter && (
        <Link
          href={getLinkHref(twitter, 'https://www.x.com/')}
          variant="standardSecondary"
          target="_blank"
          rel="noopener noreferrer"
          css={styles.linkItem}
        >
          <XIcon />
          {twitter}
        </Link>
      )}

      {youtube && (
        <Link
          href={getLinkHref(youtube, 'https://www.youtube.com/')}
          variant="standardSecondary"
          target="_blank"
          rel="noopener noreferrer"
          css={styles.linkItem}
        >
          <YouTubeIcon />
          {youtube}
        </Link>
      )}
    </div>
  );
};

export default PartnerLinksSection;
